/**
 * Uma coluna da regua: um toque, sua copy e o que aconteceu com quem o recebeu.
 *
 * Decisoes de leitura:
 *  - A COPY vem antes das metricas. O lojista precisa ver o que a pessoa
 *    recebeu para entender por que 18 clicaram e 230 responderam; numero sem
 *    mensagem e so numero.
 *  - Logo abaixo do numero principal fica a FAIXA DE EFICIENCIA do toque:
 *    conversao (quem voltou / quem recebeu), comissao por venda vinda dele e
 *    quantos toques foram precisos por venda. E o que permite comparar
 *    T+1h com T+24h de relance -- e e daqui que saem os selos "Melhor
 *    conversao" / "Menor conversao", calculados em agregacao.ts.
 *  - Cada botao da mensagem traz, logo abaixo, O QUE ELE FAZ no funil ("abre o
 *    checkout dele, com a marca da regua"; "cai na caixa do comercial e a regua
 *    para"). E a unica forma de a regra de atribuicao (ADR-001) ficar visivel
 *    exatamente onde ela acontece.
 *
 * Um marco que o funil ainda nao alcancou (empresa mais nova que D+90) mostra
 * "ainda sem como medir" em vez de zeros: zero e uma afirmacao; "sem medir" e
 * a verdade.
 */

import type { ReactNode } from 'react';
import { Badge, Footnote, Icon, MetricLine, destacarVariaveis, type BadgeTone, type IconName } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDate, formatRatio } from '../../../lib/format';
import { comissaoDoToque, voltaramNoToque } from '../agregacao';
import type { DestaquesDaRegua, FunnelKind, TouchDefinition, TouchMetrics } from '../types';
import { CANAL, VOCABULARIO, rotuloDoAtraso, rotuloDoMarco } from '../vocabulario';

function Linha({
  icon,
  valor,
  label,
  detail,
  tone,
  valorTexto,
}: {
  readonly icon: IconName;
  readonly valor: number;
  readonly label: ReactNode;
  readonly detail?: ReactNode;
  readonly tone: BadgeTone;
  /** Quando o valor e dinheiro ja formatado (a linha continua muda em zero). */
  readonly valorTexto?: string;
}): JSX.Element {
  return <MetricLine icon={icon} value={valorTexto ?? formatCount(valor)} label={label} detail={detail} tone={tone} muted={valor === 0} />;
}

function ExplicacaoDoBotao({ kind, tipo }: { readonly kind: FunnelKind; readonly tipo: 'link' | 'reply' }): JSX.Element {
  if (tipo === 'link') {
    return (
      <p className="flex items-start gap-1.5 rounded-lg bg-info/5 px-3 py-2 text-xs text-ink-muted">
        <Icon name="seta-dir" size="xs" className="mt-0.5 text-info" />
        <span>
          Abre {kind === 'expirados' ? 'a reativação' : 'o checkout dele'}, <strong className="text-ink">com a marca da régua</strong> · pagou ali ={' '}
          <strong className="text-ink">{kind === 'expirados' ? 'voltou' : 'recuperado'} pelo botão</strong>
        </span>
      </p>
    );
  }
  return (
    <p className="flex items-start gap-1.5 rounded-lg bg-series-7/5 px-3 py-2 text-xs text-ink-muted">
      <Icon name="seta-dir" size="xs" className="mt-0.5 text-series-7" />
      <span>
        Cai na <strong className="text-ink">caixa do Comercial</strong> · a régua <strong className="text-ink">para na hora</strong> · vendedora fechou em até 7 dias ={' '}
        <strong className="text-ink">{kind === 'expirados' ? 'voltou' : 'recuperado'} pela vendedora</strong>
      </span>
    </p>
  );
}

/**
 * Faixa de eficiencia do toque: tres celulas curtas. "Comissao por venda" e o
 * custo por recuperacao do ponto de vista do lojista (o que ele paga ao
 * Converte Facil por cada venda que este toque trouxe) -- comissao, nao gasto.
 */
function Eficiencia({ metrics, feeBps, expirados }: { readonly metrics: TouchMetrics; readonly feeBps: number; readonly expirados: boolean }): JSX.Element {
  const voltaram = voltaramNoToque(metrics);
  const comissao = comissaoDoToque(metrics, feeBps);
  const toquesPorVenda = voltaram === 0 ? null : Math.round(metrics.reached / voltaram);
  return (
    <dl className="grid grid-cols-3 divide-x divide-line rounded-xl border border-line bg-elevated text-center">
      <div className="px-2 py-2" title={`${expirados ? 'Voltaram' : 'Recuperados'} por este toque (pagou ou a vendedora fechou) dividido por quem o recebeu.`}>
        <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Conversão</dt>
        <dd className="text-sm font-semibold tabular-nums text-ink">{formatRatio(voltaram, metrics.reached)}</dd>
        <dd className="text-[10px] text-ink-subtle">
          {formatCount(voltaram)} de {formatCount(metrics.reached)}
        </dd>
      </div>
      <div className="px-2 py-2" title="Comissão do Converte Fácil dividida pelas vendas que este toque trouxe. É o que a empresa paga por recuperação vinda daqui.">
        <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Comissão/venda</dt>
        <dd className="text-sm font-semibold tabular-nums text-ink">{comissao.porVendaCents === null ? '—' : formatBRLFromCents(comissao.porVendaCents)}</dd>
        <dd className="text-[10px] text-ink-subtle">{comissao.totalCents > 0 ? `${formatBRLFromCents(comissao.totalCents)} no total` : 'sem venda ainda'}</dd>
      </div>
      <div className="px-2 py-2" title="Quantas pessoas precisaram receber este toque para sair uma venda.">
        <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Toques/venda</dt>
        <dd className="text-sm font-semibold tabular-nums text-ink">{toquesPorVenda === null ? '—' : `≈ ${formatCount(toquesPorVenda)}`}</dd>
        <dd className="text-[10px] text-ink-subtle">{toquesPorVenda === null ? 'sem base' : 'para 1 venda'}</dd>
      </div>
    </dl>
  );
}

export function ToqueCard({
  kind,
  touch,
  metrics,
  proximo,
  resultadosVisiveis,
  funnelSinceAt,
  feeBps,
  destaques,
}: {
  readonly kind: FunnelKind;
  readonly touch: TouchDefinition;
  readonly metrics: TouchMetrics;
  readonly proximo: TouchDefinition | null;
  readonly resultadosVisiveis: boolean;
  readonly funnelSinceAt: string;
  readonly feeBps: number;
  readonly destaques: DestaquesDaRegua;
}): JSX.Element {
  const v = VOCABULARIO[kind];
  const canal = touch.channel === 'voice' ? CANAL.voice : CANAL.whatsapp;
  const atraso = rotuloDoAtraso(kind, touch.delayMinutes);
  const marco = rotuloDoMarco(kind, touch.order, touch.delayMinutes, touch.isLast);
  const expirados = kind === 'expirados';
  const voltaram = voltaramNoToque(metrics);
  const voltaramCents = metrics.paidAmountCents + metrics.closedBySellerAmountCents;
  const melhor = destaques.melhor === touch.id;
  const pior = destaques.pior === touch.id;

  const numeroPrincipal = expirados ? metrics.queued : metrics.reached;
  const legendaPrincipal = expirados ? 'na fila hoje' : touch.channel === 'voice' ? 'pessoas ligadas pela IA' : v.recebeu;

  return (
    <article
      aria-label={`${atraso} · ${canal.label}`}
      className={`flex h-full flex-col gap-3 rounded-2xl border bg-surface p-4 shadow-soft ${
        !touch.enabled ? 'border-status-warning/50' : melhor ? 'border-status-good/50' : pior ? 'border-status-critical/40' : 'border-line'
      }`}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone="accent" icon="relogio">
            {atraso}
          </Badge>
          <Badge tone={canal.tone} icon={canal.icon}>
            {canal.label}
          </Badge>
          {touch.enabled ? null : (
            <Badge tone="warning" dot>
              Desligado na prévia
            </Badge>
          )}
          {melhor && touch.enabled ? (
            <Badge tone="good" icon="estrela">
              Melhor conversão
            </Badge>
          ) : null}
          {pior && touch.enabled ? (
            <Badge tone="critical" icon="alerta">
              Menor conversão
            </Badge>
          ) : null}
        </div>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{marco}</span>
      </header>

      <div>
        <p className="text-3xl font-semibold tabular-nums tracking-tight text-ink">{metrics.measurable ? formatCount(numeroPrincipal) : '—'}</p>
        <p className="text-xs text-ink-muted">{legendaPrincipal}</p>
      </div>

      {metrics.measurable && touch.enabled && metrics.reached > 0 ? <Eficiencia metrics={metrics} feeBps={feeBps} expirados={expirados} /> : null}

      <div className="rounded-xl border border-line bg-elevated px-3 py-3 text-sm leading-relaxed text-ink">
        {touch.channel === 'voice' ? <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Roteiro resumido da IA</p> : null}
        <p className="whitespace-pre-wrap">{destacarVariaveis(touch.body)}</p>
      </div>

      {touch.channel === 'voice' ? (
        <Footnote>
          Até {touch.maxAttempts} tentativas por pessoa (RF08). Caixa postal encerra a tentativa na hora; quem não atende recebe uma segunda ligação e depois segue para o WhatsApp.
        </Footnote>
      ) : touch.buttons.length === 0 ? (
        <Footnote>Sem botão: o {'{link}'} vai no texto e o pagamento por ele conta como recuperado pelo WhatsApp.</Footnote>
      ) : (
        <div className="flex flex-col gap-2">
          {touch.buttons.map((b) => (
            <div key={`${b.kind}-${b.label}`} className="flex flex-col gap-1">
              <p className="flex items-center justify-center gap-1.5 text-sm font-medium text-info">
                <Icon name={b.kind === 'link' ? 'externo' : 'resposta'} size="xs" />
                {b.label}
              </p>
              <ExplicacaoDoBotao kind={kind} tipo={b.kind} />
            </div>
          ))}
        </div>
      )}

      {touch.email === null ? null : (
        <section aria-label="E-mail que sai junto" className="rounded-xl border border-brand/30 bg-brand/5">
          <h4 className="flex items-center gap-1.5 border-b border-brand/20 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-brand">
            <Icon name="email" size="xs" />
            E-mail que sai junto
          </h4>
          <div className="px-3 py-3">
            <p className="text-sm font-semibold text-ink">{destacarVariaveis(touch.email.subject)}</p>
            <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-ink-muted">{destacarVariaveis(touch.email.body)}</p>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-sm font-medium text-info">
              <Icon name="externo" size="xs" />
              {touch.email.cta}
            </p>
            {metrics.email === null || !metrics.measurable ? null : (
              <p className="mt-3 flex flex-wrap gap-x-2 gap-y-0.5 border-t border-brand/20 pt-2 text-xs tabular-nums text-ink-subtle">
                <span>
                  <strong className="text-ink">{formatCount(metrics.email.sent)}</strong> enviados
                </span>
                <span>
                  <strong className="text-ink">{formatCount(metrics.email.opened)}</strong> abriram ({formatRatio(metrics.email.opened, metrics.email.sent)})
                </span>
                <span>
                  <strong className="text-ink">{formatCount(metrics.email.clicked)}</strong> clicaram
                </span>
                {metrics.email.skipped > 0 ? (
                  <span>
                    <strong className="text-ink">{formatCount(metrics.email.skipped)}</strong> pulados
                  </span>
                ) : null}
              </p>
            )}
          </div>
        </section>
      )}

      {metrics.email !== null && metrics.email.fallback > 0 ? (
        <Footnote>
          {formatCount(metrics.email.fallback)} desses foram por <strong className="text-ink-muted">e-mail de reserva</strong> (o WhatsApp não entregou).
        </Footnote>
      ) : null}

      {!metrics.measurable ? (
        <p className="rounded-lg border border-dashed border-edge px-3 py-2 text-xs text-ink-subtle">
          Ainda sem como medir: o funil começou em {formatDate(funnelSinceAt)} e quem venceu depois disso não teve tempo de chegar a {atraso}. Vai aparecendo com o passar dos dias.
        </p>
      ) : resultadosVisiveis ? (
        <div className="flex flex-col gap-1.5" aria-label={`Resultados do toque ${atraso}`}>
          {touch.channel === 'voice' && metrics.voice !== null ? (
            <>
              <Linha icon="telefone" valor={metrics.voice.calls} label="ligações feitas" detail={`${formatCount(metrics.reached)} pessoas · até ${touch.maxAttempts} tentativas`} tone="accent" />
              <Linha icon="check" valor={metrics.voice.answered} label="atenderam" detail={formatRatio(metrics.voice.answered, metrics.reached)} tone="info" />
              <Linha icon="pausa" valor={metrics.voice.voicemail} label="caixa postal" tone="warning" />
              <Linha icon="x" valor={metrics.voice.noAnswer} label="sem resposta" detail={metrics.voice.failed > 0 ? `${formatCount(metrics.voice.failed)} falharam` : undefined} tone="neutral" />
              <Linha icon="moeda" valor={metrics.voice.paidAfterCall} label="pagaram durante ou logo após a ligação" tone="good" />
            </>
          ) : null}

          {touch.channel === 'whatsapp' && expirados ? (
            <>
              <Linha icon="disparo" valor={metrics.reached} label="disparados no período" detail={metrics.failed > 0 ? <span className="text-status-critical">{formatCount(metrics.failed)} falharam</span> : undefined} tone="accent" />
              <Linha icon="atualizar" valor={voltaram} label="voltaram nessa janela" detail={voltaram > 0 ? formatBRLFromCents(voltaramCents) : undefined} tone="good" />
              <Linha icon="check" valor={metrics.delivered} label="chegaram no aparelho" tone="info" />
              <Linha icon="olho" valor={metrics.opened} label="abriram a mensagem" tone="info" />
            </>
          ) : null}

          {touch.channel === 'whatsapp' ? (
            <>
              <Linha icon="clique" valor={metrics.clicked} label="clicaram no botão" detail={metrics.clicked > 0 ? formatRatio(metrics.clicked, metrics.reached) : undefined} tone="info" />
              {expirados ? null : (
                <Linha icon="moeda" valor={metrics.paid} label="pagaram e saíram" detail={metrics.paid > 0 ? `${formatCount(metrics.paidByButton)} pelo botão` : undefined} tone="good" />
              )}
              <Linha
                icon="resposta"
                valor={metrics.replied}
                label={expirados ? 'responderam — a régua para e a vendedora assume' : 'responderam → vendedora assume'}
                detail={`${formatCount(metrics.closedBySeller)} fechadas`}
                tone="violet"
              />
              {expirados && metrics.email !== null ? (
                <>
                  <Linha icon="email" valor={metrics.email.sent} label="e-mails enviados" detail={metrics.email.fallback > 0 ? `${formatCount(metrics.email.fallback)} de reserva` : undefined} tone="good" />
                  <Linha icon="olho" valor={metrics.email.opened} label="abriram o e-mail" detail={formatRatio(metrics.email.opened, metrics.email.sent)} tone="info" />
                  <Linha icon="clique" valor={metrics.email.clicked} label="clicaram no e-mail" tone="info" />
                </>
              ) : null}
            </>
          ) : null}

          <Linha icon="saida" valor={metrics.unsubscribed} label="descadastraram" tone="neutral" />

          {expirados ? (
            <Linha icon="moeda" valor={metrics.stuckAmountCents} valorTexto={formatBRLFromCents(metrics.stuckAmountCents)} label="parados nesse marco hoje" tone="warning" />
          ) : null}

          {touch.isLast || proximo === null ? (
            <Linha icon="alerta" valor={metrics.endedWithoutReturn} label="chegaram ao fim sem voltar" tone="critical" />
          ) : (
            <>
              <Linha icon="relogio" valor={metrics.waitingNext} label="esperando o próximo" tone="warning" />
              <Linha icon="seta-dir" valor={metrics.movedOn} label={`seguiram pro ${rotuloDoAtraso(kind, proximo.delayMinutes)}`} tone="brand" />
            </>
          )}
        </div>
      ) : null}
    </article>
  );
}
